import { useState } from 'react';
import { Lock, Trash2, Pencil, Check, X, AlertTriangle } from 'lucide-react';
import type { BankBalanceEntry, BankAccount, BankComparison, UpdateEntryData } from '../../types/bankBalance';
import { ConfirmModal } from '../ui/ConfirmModal';
import { parseBankAmount } from '../../utils/bankAmountParse';

interface BankBalanceTableProps {
    entries: BankBalanceEntry[];
    bankAccounts: BankAccount[];
    comparison: BankComparison[];
    canEdit: boolean;
    onUpdate: (id: number, data: UpdateEntryData) => Promise<void>;
    onDelete: (id: number) => Promise<void>;
}

function formatAmount(amount: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('de-CH');
}

function getDelta(bankId: string, amount: number, comparison: BankComparison[]): { value: number; label: string } | null {
    const prev = comparison.find(c => c.BankID === bankId);
    if (!prev) return null;
    const delta = amount - prev.PrevAmountFCY;
    const absK = Math.abs(delta) >= 1000 ? (delta / 1000).toFixed(0) + 'K' : delta.toFixed(0);
    return { value: delta, label: delta >= 0 ? `+${absK}` : absK };
}

export function BankBalanceTable({
    entries, bankAccounts, comparison, canEdit, onUpdate, onDelete,
}: BankBalanceTableProps) {
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editData, setEditData] = useState<UpdateEntryData>({});
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    // Phone view only: the amount as typed, so a decimal comma works (type="number" would drop it).
    const [editAmountText, setEditAmountText] = useState('');

    const handleStartEdit = (entry: BankBalanceEntry) => {
        setEditingId(entry.EntryID);
        setEditAmountText(formatAmount(entry.AmountFCY));
        setEditData({
            bankId: entry.BankID,
            postingDate: entry.PostingDate.split('T')[0],
            currencyCode: entry.CurrencyCode,
            amountFCY: entry.AmountFCY,
            notes: entry.Notes,
        });
    };

    const handleSaveEdit = async () => {
        if (editingId === null) return;
        setSaving(true);
        try {
            await onUpdate(editingId, editData);
            setEditingId(null);
        } finally {
            setSaving(false);
        }
    };

    const editAmountParsed = parseBankAmount(editAmountText);

    const handleSaveEditMobile = async () => {
        if (editingId === null || editAmountParsed === null) return;
        setSaving(true);
        try {
            await onUpdate(editingId, { ...editData, amountFCY: editAmountParsed });
            setEditingId(null);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditData({});
    };

    const handleConfirmDelete = async () => {
        if (deletingId === null) return;
        await onDelete(deletingId);
        setDeletingId(null);
    };

    const handleBankChange = (bankId: string) => {
        const account = bankAccounts.find(a => a.BankID === bankId);
        setEditData(prev => ({
            ...prev,
            bankId,
            currencyCode: account?.CurrencyCode || prev.currencyCode,
        }));
    };

    if (entries.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium">No entries for this period</p>
                <p className="text-sm mt-1">Use "New Entry" or "Copy from Date" to add entries.</p>
            </div>
        );
    }

    return (
        <>
            {/* Phone: one card per entry, editing stacked instead of side by side */}
            <ul className="md:hidden divide-y divide-gray-100">
                {entries.map(entry => {
                    const isEditing = editingId === entry.EntryID;
                    const isAutomatic = entry.IsAutomatic;
                    const delta = getDelta(entry.BankID, entry.AmountFCY, comparison);
                    const by = (entry.UpdatedBy || entry.CreatedBy || '').split('@')[0];

                    if (isEditing) {
                        return (
                            <li key={entry.EntryID} className="p-3 bg-blue-50 space-y-2">
                                <label className="block text-xs font-medium text-gray-500">Date
                                    <input
                                        type="date"
                                        value={editData.postingDate || ''}
                                        onChange={e => setEditData(prev => ({ ...prev, postingDate: e.target.value }))}
                                        className="mt-1 w-full h-11 border border-gray-300 rounded-lg px-3 text-base text-gray-900 bg-white"
                                    />
                                </label>
                                <label className="block text-xs font-medium text-gray-500">Bank account
                                    <select
                                        value={editData.bankId || ''}
                                        onChange={e => handleBankChange(e.target.value)}
                                        className="mt-1 w-full h-11 border border-gray-300 rounded-lg px-3 text-base text-gray-900 bg-white"
                                    >
                                        {bankAccounts.map(a => (
                                            <option key={a.BankID} value={a.BankID}>{a.BankAccountName}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block text-xs font-medium text-gray-500">Amount ({editData.currencyCode})
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        value={editAmountText}
                                        onChange={e => setEditAmountText(e.target.value)}
                                        className={`mt-1 w-full h-12 border rounded-lg px-3 text-lg text-right tabular-nums text-gray-900 ${editAmountParsed === null ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}`}
                                        autoFocus
                                    />
                                </label>
                                {editAmountParsed === null && <p className="text-sm text-red-600">Not a valid amount</p>}
                                <div className="flex gap-2 pt-1">
                                    <button
                                        onClick={handleSaveEditMobile}
                                        disabled={saving || editAmountParsed === null}
                                        className="flex-1 h-11 flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-white font-medium disabled:bg-gray-300"
                                    >
                                        <Check className="w-4 h-4" /> Save
                                    </button>
                                    <button
                                        onClick={handleCancelEdit}
                                        className="flex-1 h-11 flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium"
                                    >
                                        <X className="w-4 h-4" /> Cancel
                                    </button>
                                </div>
                            </li>
                        );
                    }

                    return (
                        <li key={entry.EntryID} className={`p-3 ${isAutomatic ? 'bg-gray-50/50' : ''}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 font-medium text-gray-900">
                                        {isAutomatic && <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                                        <span className={`truncate ${isAutomatic ? 'text-gray-500' : ''}`}>{entry.BankAccountName || entry.BankID}</span>
                                    </div>
                                    <div className="mt-0.5 text-xs text-gray-500">
                                        {formatDate(entry.PostingDate)}{by ? ` · ${by}` : ''}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className={`tabular-nums font-semibold ${entry.AmountFCY < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                        {formatAmount(entry.AmountFCY)} <span className="text-xs font-normal text-gray-500">{entry.CurrencyCode}</span>
                                    </div>
                                    {delta && (
                                        <div className={`text-xs tabular-nums ${delta.value > 0 ? 'text-green-600' : delta.value < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                            {delta.label}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {canEdit && !isAutomatic && (
                                <div className="mt-2 flex gap-2">
                                    <button
                                        onClick={() => handleStartEdit(entry)}
                                        className="h-9 px-3 flex items-center gap-1.5 rounded-lg border border-gray-300 text-sm text-gray-700"
                                    >
                                        <Pencil className="w-3.5 h-3.5" /> Edit
                                    </button>
                                    <button
                                        onClick={() => setDeletingId(entry.EntryID)}
                                        className="h-9 px-3 flex items-center gap-1.5 rounded-lg border border-gray-300 text-sm text-gray-500"
                                        aria-label="Delete"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left px-3 py-2 font-medium text-gray-600 w-28">Date</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Bank Account</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600 w-36">Amount FCY</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600 w-24">&Delta; Prev</th>
                            <th className="text-center px-3 py-2 font-medium text-gray-600 w-16">CCY</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 w-32">Changed By</th>
                            {canEdit && <th className="text-center px-3 py-2 font-medium text-gray-600 w-20">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map(entry => {
                            const isEditing = editingId === entry.EntryID;
                            const isAutomatic = entry.IsAutomatic;
                            const delta = getDelta(entry.BankID, entry.AmountFCY, comparison);

                            return (
                                <tr
                                    key={entry.EntryID}
                                    className={`border-b border-gray-100 ${isAutomatic ? 'bg-gray-50/50' : 'hover:bg-blue-50/30'} ${isEditing ? 'bg-blue-50' : ''}`}
                                >
                                    {/* Date */}
                                    <td className="px-3 py-2">
                                        {isEditing ? (
                                            <input
                                                type="date"
                                                value={editData.postingDate || ''}
                                                onChange={e => setEditData(prev => ({ ...prev, postingDate: e.target.value }))}
                                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            />
                                        ) : (
                                            <span className="tabular-nums">{formatDate(entry.PostingDate)}</span>
                                        )}
                                    </td>

                                    {/* Bank Account */}
                                    <td className="px-3 py-2">
                                        {isEditing ? (
                                            <select
                                                value={editData.bankId || ''}
                                                onChange={e => handleBankChange(e.target.value)}
                                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            >
                                                {bankAccounts.map(a => (
                                                    <option key={a.BankID} value={a.BankID}>
                                                        {a.BankAccountName}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <div className="flex items-center gap-1.5">
                                                {isAutomatic && <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                                                <span className={isAutomatic ? 'text-gray-500' : ''}>
                                                    {entry.BankAccountName || entry.BankID}
                                                </span>
                                            </div>
                                        )}
                                    </td>

                                    {/* Amount */}
                                    <td className="px-3 py-2 text-right">
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={editData.amountFCY ?? ''}
                                                onChange={e => setEditData(prev => ({ ...prev, amountFCY: parseFloat(e.target.value) || 0 }))}
                                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                                                autoFocus
                                            />
                                        ) : (
                                            <span className={`tabular-nums font-medium ${entry.AmountFCY < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                                {formatAmount(entry.AmountFCY)}
                                            </span>
                                        )}
                                    </td>

                                    {/* Delta */}
                                    <td className="px-3 py-2 text-right">
                                        {delta && (
                                            <span className={`text-xs tabular-nums ${delta.value > 0 ? 'text-green-600' : delta.value < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                                {delta.label}
                                            </span>
                                        )}
                                    </td>

                                    {/* Currency */}
                                    <td className="px-3 py-2 text-center text-gray-500">
                                        {isEditing ? editData.currencyCode : entry.CurrencyCode}
                                    </td>

                                    {/* Changed By */}
                                    <td className="px-3 py-2 text-gray-500 text-xs truncate max-w-[120px]" title={entry.UpdatedBy || entry.CreatedBy}>
                                        {(entry.UpdatedBy || entry.CreatedBy || '').split('@')[0]}
                                    </td>

                                    {/* Actions */}
                                    {canEdit && (
                                        <td className="px-3 py-2 text-center">
                                            {isAutomatic ? (
                                                <span className="text-xs text-gray-400">auto</span>
                                            ) : isEditing ? (
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        onClick={handleSaveEdit}
                                                        disabled={saving}
                                                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                                                        title="Save"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={handleCancelEdit}
                                                        className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                                                        title="Cancel"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        onClick={() => handleStartEdit(entry)}
                                                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                        title="Edit"
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeletingId(entry.EntryID)}
                                                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Stale accounts indicator */}
            {comparison.length > 0 && (() => {
                const entryBankIds = new Set(entries.map(e => e.BankID));
                const missingBanks = comparison.filter(c => !entryBankIds.has(c.BankID));
                if (missingBanks.length === 0) return null;
                return (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 text-amber-700 text-sm">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            <span>
                                <strong>{missingBanks.length} bank account{missingBanks.length > 1 ? 's' : ''}</strong> had entries last month but not yet this month.
                            </span>
                        </div>
                    </div>
                );
            })()}

            {deletingId !== null && (
                <ConfirmModal
                    title="Delete Entry"
                    message="Are you sure you want to delete this bank balance entry? This action cannot be undone."
                    confirmLabel="Delete"
                    variant="danger"
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setDeletingId(null)}
                />
            )}
        </>
    );
}
